// @flow
import zentakeApi from 'api'
import React from 'react'
import { toast } from 'react-toastify'
import type { PacketResponse } from 'types/packet-response'
import type { Response } from 'types/response'
import setFormResponsesAsSubmitted from 'utils/FillForms/helpers'
import { ZentakeContext } from 'utils/ZentakeContext'

function useResponseManualSubmit(
  response: ?Response,
  setResponse: ?(Response) => void,
  packetResponse: ?PacketResponse,
  setPacketResponse: ?(PacketResponse) => void
): [
  boolean,
  {
    title: string,
    onClick: () => void,
    loading: boolean,
  },
] {
  const { setAlertConfig } = React.useContext(ZentakeContext)

  const [isManuallySubmitting, setIsManuallySubmitting] = React.useState(false)

  const onManuallySubmitResponse = React.useCallback(async () => {
    if (!response || !setResponse) return

    const responseData = await zentakeApi.submitResponse(
      response?.uid || response?.id,
      'manually',
      true
    )

    setResponse({ ...response, ...responseData })
  }, [response, setResponse])

  const onManuallySubmitPacketResponse = React.useCallback(async () => {
    if (!packetResponse?.id || !setPacketResponse) return

    await setFormResponsesAsSubmitted(packetResponse, 'manually', true)
    const responseData = await zentakeApi.submitResponsePacket(
      packetResponse.id,
      'manually',
      true
    )

    setPacketResponse({ ...packetResponse, ...responseData })
  }, [packetResponse, setPacketResponse])

  const onManuallySubmit = React.useCallback(async () => {
    if (isManuallySubmitting) return

    setIsManuallySubmitting(true)

    if (response) {
      await onManuallySubmitResponse()
    } else if (packetResponse) {
      await onManuallySubmitPacketResponse()
    }

    toast.success('Response submitted')
    setIsManuallySubmitting(false)
  }, [
    isManuallySubmitting,
    onManuallySubmitPacketResponse,
    onManuallySubmitResponse,
    packetResponse,
    response,
  ])

  const onManuallySubmitRequest = React.useCallback(() => {
    setAlertConfig({
      title: 'Submit Form',
      description:
        "Your client won't be able to make changes to this form once it's submitted. Are you sure you want to submit this form?",
      buttons: [
        { title: 'Cancel' },
        { title: 'Submit', style: 'primary', onPress: onManuallySubmit },
      ],
      canDismiss: false,
    })
  }, [onManuallySubmit, setAlertConfig])

  const isSubmitted = Boolean(
    response?.is_submitted || packetResponse?.is_submitted
  )

  return [
    isSubmitted,
    {
      title: 'Manually Submit',
      onClick: onManuallySubmitRequest,
      loading: isManuallySubmitting,
    },
  ]
}

export default useResponseManualSubmit
